import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Tldraw, useEditor } from 'tldraw';
import 'tldraw/tldraw.css';
import { PenTool, Plus, X, Loader2, Pencil, Youtube, Video as VideoIcon, MonitorPlay, Square, ExternalLink } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';

/**
 * Phase 3A-2 collaborative boards:
 *  - Multiple board types: whiteboard (tldraw), youtube, vimeo, screen-share
 *  - Multi-board tabs (CRUD via REST)
 *  - Persistent snapshots for whiteboard
 *  - Live sync via Daily app messages
 */

// ------------------------------ Whiteboard board (tldraw) ----------------------------
// Module-level registry: board_id → tldraw editor instance. Used by RecapPanel to
// rasterise the currently-mounted whiteboards as PNG snapshots without touching
// the editor JSX tree.
const __WHITEBOARD_EDITORS = new Map();
export function getMountedWhiteboardEditors() {
  return new Map(__WHITEBOARD_EDITORS);
}

const SyncBridge = ({ board, roomId, token, callObject, role }) => {
  const editor = useEditor();
  const applyingRemoteRef = useRef(false);
  const lastSentRef = useRef(0);
  const saveTimerRef = useRef(null);

  // Register the editor instance so RecapPanel can grab it for PNG export
  useEffect(() => {
    if (!editor) return;
    __WHITEBOARD_EDITORS.set(board.board_id, editor);
    return () => { __WHITEBOARD_EDITORS.delete(board.board_id); };
  }, [editor, board.board_id]);

  useEffect(() => {
    if (!callObject) return;
    const handler = (e) => {
      try {
        const m = e?.data;
        if (!m || m.kind !== 'wh-board-snapshot') return;
        if (m.board_id !== board.board_id) return;
        applyingRemoteRef.current = true;
        editor.store.loadSnapshot(m.snapshot);
      } catch (_err) {} finally {
        setTimeout(() => { applyingRemoteRef.current = false; }, 50);
      }
    };
    callObject.on('app-message', handler);
    return () => { try { callObject.off('app-message', handler); } catch (_e) {} };
  }, [callObject, editor, board.board_id]);

  useEffect(() => {
    if (!editor) return;
    const onChange = () => {
      if (applyingRemoteRef.current) return;
      const now = Date.now();
      if (now - lastSentRef.current < 250) return;
      lastSentRef.current = now;
      const snap = editor.store.getSnapshot();
      try {
        callObject?.sendAppMessage({ kind: 'wh-board-snapshot', board_id: board.board_id, snapshot: snap });
      } catch (_e) {}
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        classroomApi.boardSnapshotSave(roomId, token, board.board_id, snap).catch(() => {});
      }, 1200);
    };
    const cleanup = editor.store.listen(onChange, { source: 'user', scope: 'document' });
    return () => { cleanup?.(); if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [editor, callObject, board.board_id, roomId, token]);

  useEffect(() => {
    let cancelled = false;
    classroomApi.boardSnapshotLoad(roomId, token, board.board_id)
      .then((r) => {
        if (cancelled || !r?.snapshot) return;
        applyingRemoteRef.current = true;
        try { editor.store.loadSnapshot(r.snapshot); } catch (_e) {}
        setTimeout(() => { applyingRemoteRef.current = false; }, 100);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [editor, roomId, token, board.board_id]);

  useEffect(() => {
    if (!editor) return;
    editor.updateInstanceState({ isReadonly: role !== 'tutor' });
  }, [editor, role]);

  return null;
};

const WhiteboardBoard = ({ board, roomId, token, callObject, role }) => (
  <Tldraw key={board.board_id} persistenceKey={`winhouse-${roomId}-${board.board_id}`}>
    <SyncBridge board={board} roomId={roomId} token={token} callObject={callObject} role={role} />
  </Tldraw>
);

// ------------------------------ Embed boards (YouTube / Vimeo) ----------------------
const EmbedBoard = ({ board, roomId, token, role }) => {
  const [editing, setEditing] = useState(!board.source_url);
  const [draft, setDraft] = useState(board.source_url || '');
  const [busy, setBusy] = useState(false);

  const isTutor = role === 'tutor';

  const submit = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await classroomApi.boardUpdateSource(roomId, token, board.board_id, draft.trim());
      // Mutate the board reference inline; parent will refetch on next nav
      board.source_url = draft.trim();
      setEditing(false);
      toast.success('Video updated');
    } catch (e) { toast.error(e.message || 'Could not save URL'); }
    finally { setBusy(false); }
  };

  if (!board.source_url && !isTutor) {
    return <div className="h-full w-full flex items-center justify-center text-xs text-gray-400 p-8 text-center">Your tutor hasn't picked a video yet</div>;
  }

  if (editing && isTutor) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center bg-black/95 p-6" data-testid={`embed-edit-${board.board_id}`}>
        <div className="text-white text-sm font-bold mb-3">Paste a {board.type === 'youtube' ? 'YouTube' : 'Vimeo'} link</div>
        <div className="text-white/60 text-xs mb-4">e.g. {board.type === 'youtube' ? 'https://www.youtube.com/watch?v=…' : 'https://vimeo.com/…'}</div>
        <div className="flex w-full max-w-xl gap-2">
          <input value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
            placeholder="Paste URL here"
            className="flex-1 h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white text-xs focus:outline-none focus:ring-2 focus:ring-red-700"
            data-testid={`embed-url-input-${board.board_id}`} />
          <button onClick={submit} disabled={busy || !draft.trim()}
            className="h-10 px-4 rounded-lg bg-red-700 hover:bg-red-800 disabled:opacity-40 text-white text-xs font-bold uppercase tracking-wider"
            data-testid={`embed-url-save-${board.board_id}`}>
            {busy ? '…' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full bg-black" data-testid={`embed-${board.type}-${board.board_id}`}>
      <iframe
        src={board.source_url}
        title={board.name}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
      {isTutor && (
        <button onClick={() => { setDraft(board.source_url); setEditing(true); }}
          className="absolute top-2 right-2 h-8 px-3 rounded-lg bg-black/70 hover:bg-black/90 text-white text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
          data-testid={`embed-change-${board.board_id}`}>
          <ExternalLink className="h-3 w-3" /> Change URL
        </button>
      )}
    </div>
  );
};

// ------------------------------ Screen-share board -----------------------------------
const ScreenShareBoard = ({ callObject, role }) => {
  const videoRef = useRef(null);
  const [hasScreen, setHasScreen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isTutor = role === 'tutor';

  // Listen for screen-share track add/remove
  useEffect(() => {
    if (!callObject) return;
    const detectScreen = () => {
      try {
        const ps = callObject.participants();
        let track = null;
        Object.values(ps).forEach((p) => {
          if (p.tracks?.screenVideo?.state === 'playable' && p.tracks.screenVideo.persistentTrack) {
            track = p.tracks.screenVideo.persistentTrack;
          }
        });
        if (track && videoRef.current) {
          if (videoRef.current.srcObject !== track) {
            const stream = new MediaStream([track]);
            videoRef.current.srcObject = stream;
            videoRef.current.play?.().catch(() => {});
          }
          setHasScreen(true);
        } else {
          if (videoRef.current) videoRef.current.srcObject = null;
          setHasScreen(false);
        }
      } catch (_e) {}
    };
    detectScreen();
    callObject.on('track-started', detectScreen);
    callObject.on('track-stopped', detectScreen);
    callObject.on('participant-updated', detectScreen);
    return () => {
      try {
        callObject.off('track-started', detectScreen);
        callObject.off('track-stopped', detectScreen);
        callObject.off('participant-updated', detectScreen);
      } catch (_e) {}
    };
  }, [callObject]);

  const startShare = async () => {
    if (!callObject) { toast.error('Video not ready'); return; }
    setBusy(true);
    try { await callObject.startScreenShare(); toast.success('Screen share started'); }
    catch (e) { toast.error('Could not start screen share'); }
    finally { setBusy(false); }
  };
  const stopShare = async () => {
    try { await callObject?.stopScreenShare(); } catch (_e) {}
  };

  return (
    <div className="relative h-full w-full bg-black flex items-center justify-center" data-testid="screen-share-board">
      <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full ${hasScreen ? '' : 'hidden'}`} />
      {!hasScreen && (
        <div className="text-center px-6 max-w-md">
          <MonitorPlay className="h-12 w-12 text-white/30 mx-auto mb-3" />
          <div className="text-white text-sm font-bold mb-2">Screen share board</div>
          <div className="text-white/60 text-xs mb-4">
            {isTutor
              ? 'Click "Start sharing" to broadcast your screen onto this board for your student.'
              : 'Waiting for your tutor to start sharing their screen.'}
          </div>
          {isTutor && (
            <button onClick={callObject ? startShare : () => toast.error('Live video must be active to share your screen')} disabled={busy || !callObject}
              className="h-10 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="screen-share-start">
              {busy ? 'Starting…' : !callObject ? 'Live video required' : 'Start sharing'}
            </button>
          )}
        </div>
      )}
      {hasScreen && isTutor && (
        <button onClick={stopShare}
          className="absolute top-2 right-2 h-8 px-3 rounded-lg bg-black/70 hover:bg-black/90 text-white text-[10px] uppercase tracking-wider font-bold"
          data-testid="screen-share-stop">
          Stop sharing
        </button>
      )}
    </div>
  );
};

// ------------------------------ Tab UI & Add menu ------------------------------------
const TYPE_ICONS = {
  whiteboard: Square,
  youtube: Youtube,
  vimeo: VideoIcon,
  screen: MonitorPlay,
};

const BoardTab = ({ board, active, onSelect, onDelete, onRename, isTutor }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(board.name);
  const submit = async () => {
    setEditing(false);
    const v = name.trim();
    if (!v || v === board.name) { setName(board.name); return; }
    await onRename(board.board_id, v);
  };
  const Icon = TYPE_ICONS[board.type] || Square;
  return (
    <div className={`group flex items-center gap-1.5 pl-2 pr-1.5 h-8 rounded-lg cursor-pointer text-xs font-semibold uppercase tracking-wider transition
      ${active ? 'bg-red-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
      onClick={() => !editing && onSelect(board.board_id)}
      data-testid={`board-tab-${board.board_id}`}>
      <Icon className="h-3 w-3 flex-shrink-0" />
      {editing ? (
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
          onBlur={submit}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') { setName(board.name); setEditing(false); } }}
          className="h-6 bg-transparent border-b border-white/60 outline-none text-xs w-24"
          onClick={(e) => e.stopPropagation()} />
      ) : (
        <span className="max-w-[120px] truncate">{board.name}</span>
      )}
      {isTutor && active && !editing && (
        <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          className="opacity-70 hover:opacity-100 w-5 h-5 flex items-center justify-center">
          <Pencil className="h-3 w-3" />
        </button>
      )}
      {isTutor && active && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(board.board_id); }}
          className="opacity-70 hover:opacity-100 w-5 h-5 flex items-center justify-center"
          data-testid={`board-tab-del-${board.board_id}`}>
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

const AddBoardMenu = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(null);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const close = () => { setOpen(false); setType(null); setName(''); setUrl(''); };

  const submit = async () => {
    setBusy(true);
    try {
      await onAdd({
        type,
        name: name.trim() || (type === 'whiteboard' ? 'Whiteboard' : type === 'youtube' ? 'YouTube' : type === 'vimeo' ? 'Vimeo' : 'Screen'),
        source_url: (type === 'youtube' || type === 'vimeo') ? url.trim() : '',
      });
      close();
    } catch (_e) {} finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)}
        className="h-8 w-8 rounded-lg bg-black text-white hover:bg-gray-900 flex items-center justify-center"
        data-testid="board-add">
        <Plus className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 z-30 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-3"
             data-testid="board-add-menu" onClick={(e) => e.stopPropagation()}>
          {!type ? (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold px-2 pb-1">Add a board</div>
              {[
                { k: 'whiteboard', label: 'Whiteboard', icon: Square, hint: 'Free-draw collaborative canvas' },
                { k: 'youtube', label: 'YouTube', icon: Youtube, hint: 'Embed a video lecture' },
                { k: 'vimeo', label: 'Vimeo', icon: VideoIcon, hint: 'Embed a Vimeo video' },
                { k: 'screen', label: 'Screen share', icon: MonitorPlay, hint: 'Broadcast your screen as a board' },
              ].map(({ k, label, icon: Icon, hint }) => (
                <button key={k} onClick={() => { setType(k); setName(label); }}
                  className="w-full px-2 py-2 text-left rounded-lg hover:bg-gray-100 flex items-center gap-2"
                  data-testid={`board-add-type-${k}`}>
                  <Icon className="h-4 w-4 text-red-700 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-xs font-bold text-gray-900">{label}</div>
                    <div className="text-[10px] text-gray-500">{hint}</div>
                  </div>
                </button>
              ))}
              <button onClick={close} className="w-full mt-1 text-[10px] text-gray-400 uppercase tracking-wider py-1">Cancel</button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">New {type} board</div>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Board name"
                className="w-full h-9 px-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-red-200"
                data-testid="board-add-name" />
              {(type === 'youtube' || type === 'vimeo') && (
                <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={`Paste ${type} URL`}
                  className="w-full h-9 px-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-red-200"
                  data-testid="board-add-url" />
              )}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={() => setType(null)}
                  className="h-8 px-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-[10px] uppercase tracking-wider font-bold">Back</button>
                <button onClick={submit}
                  disabled={busy || ((type === 'youtube' || type === 'vimeo') && !url.trim())}
                  className="ml-auto h-8 px-3 rounded-lg bg-red-700 hover:bg-red-800 text-white text-[10px] uppercase tracking-wider font-bold disabled:opacity-40"
                  data-testid="board-add-submit">
                  {busy ? '…' : 'Add'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ------------------------------ Panel root -------------------------------------------
const MultiBoardPanel = ({ roomId, token, role, callObject }) => {
  const [boards, setBoards] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

  const isTutor = role === 'tutor';

  const refresh = useCallback(async () => {
    try {
      const r = await classroomApi.boardsList(roomId, token);
      setBoards(r.items || []);
      setActiveId((prev) => prev || r.items?.[0]?.board_id);
    } catch (_e) {} finally { setLoading(false); }
  }, [roomId, token]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!callObject) return;
    const handler = (e) => {
      const m = e?.data;
      if (!m) return;
      if (m.kind === 'wh-boards-refresh') refresh();
      if (m.kind === 'wh-active-board' && m.board_id) setActiveId(m.board_id);
    };
    callObject.on('app-message', handler);
    return () => { try { callObject.off('app-message', handler); } catch (_e) {} };
  }, [callObject, refresh]);

  const broadcast = (kind, extra = {}) => {
    try { callObject?.sendAppMessage({ kind, ...extra }); } catch (_e) {}
  };

  const addBoard = async (payload) => {
    try {
      const r = await classroomApi.boardCreate(roomId, token, payload);
      setBoards((b) => [...b, r]);
      setActiveId(r.board_id);
      broadcast('wh-boards-refresh');
      broadcast('wh-active-board', { board_id: r.board_id });
    } catch (e) { toast.error(e.message || 'Could not add board'); throw e; }
  };
  const renameBoard = async (id, name) => {
    try {
      await classroomApi.boardRename(roomId, token, id, name);
      setBoards((bs) => bs.map((b) => b.board_id === id ? { ...b, name } : b));
      broadcast('wh-boards-refresh');
    } catch (e) { toast.error(e.message || 'Rename failed'); }
  };
  const deleteBoard = async (id) => {
    if (!window.confirm('Delete this board? Drawings will be lost.')) return;
    try {
      await classroomApi.boardDelete(roomId, token, id);
      const next = boards.filter((b) => b.board_id !== id);
      setBoards(next);
      if (activeId === id && next[0]) setActiveId(next[0].board_id);
      broadcast('wh-boards-refresh');
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };
  const selectBoard = (id) => {
    setActiveId(id);
    if (isTutor) broadcast('wh-active-board', { board_id: id });
  };

  const active = boards.find((b) => b.board_id === activeId);

  return (
    <div className="bg-[#FAF9F6] border border-gray-200 rounded-2xl overflow-hidden" data-testid="classroom-whiteboard">
      <div className="px-4 py-2 border-b border-gray-100 bg-white flex items-center gap-2 flex-wrap">
        <PenTool className="h-4 w-4 text-red-700 flex-shrink-0" />
        <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex-shrink-0">Boards</div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="board-tabs-list">
          {boards.map((b) => (
            <BoardTab key={b.board_id} board={b} active={b.board_id === activeId}
              onSelect={selectBoard} onDelete={deleteBoard} onRename={renameBoard} isTutor={isTutor} />
          ))}
        </div>
        {isTutor && <AddBoardMenu onAdd={addBoard} />}
        <span className="ml-auto text-[10px] text-gray-400 uppercase tracking-wider">{role === 'tutor' ? 'Edit' : 'View'}</span>
      </div>
      <div style={{ position: 'relative', width: '100%', height: 520 }} data-testid="classroom-whiteboard-canvas">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>
        ) : active ? (
          active.type === 'youtube' || active.type === 'vimeo' ? (
            <EmbedBoard board={active} roomId={roomId} token={token} role={role} />
          ) : active.type === 'screen' ? (
            <ScreenShareBoard callObject={callObject} role={role} />
          ) : (
            <WhiteboardBoard board={active} roomId={roomId} token={token} callObject={callObject} role={role} />
          )
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">No boards yet</div>
        )}
      </div>
    </div>
  );
};

export default MultiBoardPanel;
